/*
 * external-cart-sync.js
 *
 * Keeps the cart drawer in sync when a THIRD-PARTY app (e.g. Koala free-gifts,
 * which injects `__koala_gift` line items) mutates the cart directly via AJAX.
 *
 * The theme re-renders the drawer only in response to `PUB_SUB_EVENTS.cartUpdate`
 * (see CartDrawerItems.onCartUpdate in cart.js). The theme's own cart writes
 * publish that event themselves, but external apps do not — so their added lines
 * live in /cart.js yet never get a <tr> rendered until a full page reload.
 *
 * This script watches for completed cart-mutation requests (fetch + XHR) to
 * Shopify's /cart/{add,change,update,clear} endpoints and, when the theme did NOT
 * already announce that change, publishes a synthetic `cartUpdate` so the existing
 * re-render pipeline runs. Theme-initiated writes are suppressed (they publish
 * their own cartUpdate within the debounce window), so there is no double render.
 */
(function () {
  'use strict';

  // Shopify cart WRITE endpoints (locale prefix tolerated: /en/cart/add.js ...).
  // Reads like /cart.js and /cart?section_id=... intentionally do NOT match, so
  // the theme's own re-render fetch can never re-trigger this (no loop).
  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/i;

  // How long to wait after a mutation for the theme to announce it itself.
  var DEBOUNCE_MS = 500;

  var lastMutationAt = 0; // when an external-or-theme cart write last completed
  var lastCartUpdateAt = 0; // when a cartUpdate was last published (by anyone)
  var pendingTimer = null;

  function now() {
    return Date.now();
  }

  function ready() {
    return typeof publish === 'function' && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  }

  function isMutationUrl(url) {
    if (!url) return false;
    try {
      return MUTATION_RE.test(String(url));
    } catch (e) {
      return false;
    }
  }

  // A cart write finished. Remember when, and schedule a check. If no cartUpdate
  // fires before the check (i.e. an external app made the change), we publish one.
  function noteMutation() {
    lastMutationAt = now();
    if (pendingTimer) clearTimeout(pendingTimer);
    var mutationAt = lastMutationAt;
    pendingTimer = setTimeout(function () {
      pendingTimer = null;
      if (!ready()) return;
      // The theme already re-rendered for this change — nothing to do.
      if (lastCartUpdateAt >= mutationAt) return;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external-cart-sync' });
    }, DEBOUNCE_MS);
  }

  function start() {
    // Track every cartUpdate so we can tell whether the theme handled a mutation.
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      lastCartUpdateAt = now();
    });

    // ---- patch fetch ----
    if (typeof window.fetch === 'function') {
      var originalFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url;
        var watched = isMutationUrl(url);
        var result = originalFetch.apply(this, arguments);
        if (watched && result && typeof result.then === 'function') {
          result.then(
            function () {
              noteMutation();
            },
            function () {
              /* request failed — cart unchanged, ignore */
            },
          );
        }
        return result;
      };
    }

    // ---- patch XMLHttpRequest ----
    if (typeof window.XMLHttpRequest === 'function') {
      var XHR = window.XMLHttpRequest;
      var open = XHR.prototype.open;
      var send = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        this.__ecsWatched = isMutationUrl(url);
        return open.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        if (this.__ecsWatched) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) noteMutation();
          });
        }
        return send.apply(this, arguments);
      };
    }
  }

  // pubsub.js is deferred; wait until publish/subscribe exist before wiring up.
  (function waitForPubSub(attempt) {
    if (ready()) {
      start();
      return;
    }
    if (attempt > 50) return; // ~5s; give up quietly
    setTimeout(function () {
      waitForPubSub(attempt + 1);
    }, 100);
  })(0);
})();
